import { Box, FormModal, SpinnerButton, TextField, ValueAutocompleteField } from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { useIssuerOwnersQuery, useUpdateIssuerPartnershipsCoverageMutation } from 'graphql/bindings/AdminBindings'

import { AdminField } from 'components/shared/dropdowns'
import { MutationResult } from 'react-apollo'
import { filterOwnersByGroupAndStripIds } from 'util/OwnerUtils'
import { relationshipStrengthOpts, sensitivityOpts } from 'util/Enums'
import React from 'react'

interface FormShape {
  id: string
  owner?: { label: string; value: number }
  additional_owners: { label: string; value: number }[]
  partnerships_relationship_strength?: string
  partnerships_relationship_sensitivity?: string
  partnerships_relationship_notes?: string
  partnerships_notes?: string
  mutationResult: MutationResult
}

const PartnershipsForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, form, values, valid }) => {
  const mutateInProgress = values.mutationResult.called
  return (
    <form onSubmit={handleSubmit}>
      <AdminField name="owner" label="Partnerships owner" />
      <AdminField name="additional_owners" label="Additional Forge contact(s)" multiple />
      <ValueAutocompleteField
        name="partnerships_relationship_strength"
        label="Relationship strength"
        options={relationshipStrengthOpts}
      />
      <ValueAutocompleteField
        name="partnerships_relationship_sensitivity"
        label="Relationship sensitivity"
        options={sensitivityOpts}
      />
      {values.partnerships_relationship_sensitivity === 'high' && (
        <Field
          name="partnerships_relationship_notes"
          label="Please describe sensitivity"
          component={TextField}
          fullWidth
        />
      )}
      <Field name="partnerships_notes" label="Additional information" component={TextField} multiline fullWidth />
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

export const IssuerPartnershipsModal: React.FC<{ hideModal: () => void; issuerId: string }> = ({
  hideModal,
  issuerId,
}) => {
  const { data } = useIssuerOwnersQuery({ variables: { issuerId: issuerId } })
  const account = data?.account
  const [owner, ...additionalOwners] = filterOwnersByGroupAndStripIds(
    account?.owners ?? [],
    'partnerships'
  ).map(owner => ({ label: owner.name!, value: owner.owner_id! }))
  const [mutate, mutateResult] = useUpdateIssuerPartnershipsCoverageMutation({
    onCompleted: hideModal,
  })
  const onSubmit = (values: FormShape) => {
    const allOwners = [values.owner, ...values.additional_owners].filter(owner => !!owner)
    mutate({
      variables: {
        form: {
          id: values.id,
          partnerships_relationship_notes: values.partnerships_relationship_notes,
          partnerships_relationship_strength: values.partnerships_relationship_strength,
          partnerships_relationship_sensitivity: values.partnerships_relationship_sensitivity,
          owners: allOwners.map(owner => owner!.value),
          partnerships_notes: values.partnerships_notes,
        },
      },
    })
  }

  const initialValues: FormShape = {
    id: account?.id ?? '',
    owner: owner,
    additional_owners: additionalOwners,
    partnerships_relationship_strength: account?.partnerships_relationship_strength ?? undefined,
    partnerships_relationship_sensitivity: account?.partnerships_relationship_sensitivity ?? undefined,
    partnerships_relationship_notes: account?.partnerships_relationship_notes ?? '',
    partnerships_notes: account?.partnerships_notes ?? '',
    mutationResult: mutateResult,
  }
  return (
    <FormModal open hideModal={hideModal} title="Edit: Partnership Coverage">
      <Form<FormShape>
        component={PartnershipsForm}
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}
